import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecureProfilePhoto } from '@/components/profile/SecureProfilePhoto';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Trophy, 
  Users, 
  Calendar,
  Target,
  MapPin,
  Star,
  Award,
  Camera,
  Video,
  UserPlus,
  UserMinus
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UserActivity {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  activity_type: string;
  activity_title: string;
  activity_description?: string;
  activity_data: any;
  created_at: string;
  sport_id?: number;
  team_id?: number;
  game_id?: string;
  venue_id?: string;
  user_name?: string;
  user_avatar?: string;
  user_role?: string;
}

interface UserFeedProps {
  userId: string;
  userType: 'supabase' | 'internal';
}

export const UserFeed: React.FC<UserFeedProps> = ({ userId, userType }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [followedUsers, setFollowedUsers] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    loadFollowedUsers();
    loadFeedData();
  }, [userId, userType]);

  const loadFollowedUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_follows')
        .select('followed_id')
        .eq('follower_id', userId)
        .eq('follower_type', userType)
        .eq('is_active', true);

      if (error) throw error;
      setFollowedUsers(data.map(f => f.followed_id));
    } catch (error) {
      console.error('Error loading followed users:', error);
    }
  };

  const loadFeedData = async () => {
    try {
      setLoading(true);

      // Get followed users
      const { data: follows, error: followsError } = await supabase
        .from('user_follows')
        .select('followed_id, followed_type')
        .eq('follower_id', userId)
        .eq('follower_type', userType)
        .eq('is_active', true);

      if (followsError) throw followsError;

      if (!follows || follows.length === 0) {
        setActivities([]);
        setLoading(false);
        return;
      }

      // Get activities from followed users
      const followedIds = follows.map(f => f.followed_id);
      const { data: activities, error: activitiesError } = await supabase
        .from('user_activity_feed')
        .select('*')
        .in('user_id', followedIds)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(50);

      if (activitiesError) throw activitiesError;

      // Enrich activities with user data
      const enrichedActivities = await Promise.all(
        activities.map(async (activity) => {
          try {
            let userData = { name: 'Unknown User', avatar: '', role: '' };
            
            if (activity.user_type === 'supabase') {
              const { data: profile } = await supabase
                .from('profiles')
                .select('first_name, last_name, username, role, profile_photo_url')
                .eq('id', activity.user_id)
                .single();
              
              if (profile) {
                userData = {
                  name: profile.username || `${profile.first_name} ${profile.last_name}`.trim() || 'User',
                  avatar: profile.profile_photo_url || '',
                  role: profile.role || ''
                };
              }
            } else {
              // For internal users, get data from both tables
              const { data: internalUser } = await supabase
                .from('internal_users')
                .select('username, role')
                .eq('id', activity.user_id)
                .single();
              
              const { data: profile } = await supabase
                .from('internal_user_profiles')
                .select('first_name, last_name, username, profile_photo_url')
                .eq('user_id', activity.user_id)
                .single();
              
              if (internalUser) {
                userData = {
                  name: (profile?.username || internalUser.username) || 
                        `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'User',
                  avatar: profile?.profile_photo_url || '',
                  role: internalUser.role || ''
                };
              }
            }

            return {
              ...activity,
              user_type: activity.user_type as 'supabase' | 'internal',
              user_name: userData.name,
              user_avatar: userData.avatar,
              user_role: userData.role
            } as UserActivity;
          } catch (error) {
            console.error('Error enriching activity:', error);
            return {
              ...activity,
              user_type: activity.user_type as 'supabase' | 'internal',
              user_name: 'Unknown User',
              user_avatar: '',
              user_role: ''
            } as UserActivity;
          }
        })
      );

      setActivities(enrichedActivities);
    } catch (error) {
      console.error('Error loading feed data:', error);
      toast({
        title: "Error",
        description: "Failed to load activity feed",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (activityType: string) => {
    const icons = {
      'game_joined': Calendar,
      'game_created': Calendar,
      'team_joined': Users,
      'team_left': UserMinus,
      'achievement': Award,
      'photo_upload': Camera,
      'video_upload': Video,
      'profile_update': UserPlus,
      'game_score': Trophy,
      'new_connection': UserPlus,
      'followed_user': UserPlus,
      'venue_checkin': MapPin,
      'spotlight': Star,
      'goal_scored': Target,
      'assist': Users,
      'save': Target,
      'default': Star
    };
    return icons[activityType] || icons.default;
  };

  const getActivityColor = (activityType: string) => {
    const colors = {
      'game_joined': 'text-blue-400 bg-blue-400/10',
      'game_created': 'text-teal-400 bg-teal-400/10',
      'team_joined': 'text-green-400 bg-green-400/10',
      'team_left': 'text-red-400 bg-red-400/10',
      'achievement': 'text-yellow-400 bg-yellow-400/10',
      'photo_upload': 'text-purple-400 bg-purple-400/10',
      'video_upload': 'text-pink-400 bg-pink-400/10',
      'profile_update': 'text-cyan-400 bg-cyan-400/10',
      'game_score': 'text-orange-400 bg-orange-400/10',
      'new_connection': 'text-indigo-400 bg-indigo-400/10',
      'followed_user': 'text-indigo-400 bg-indigo-400/10',
      'venue_checkin': 'text-emerald-400 bg-emerald-400/10',
      'spotlight': 'text-yellow-300 bg-yellow-300/10',
      'goal_scored': 'text-red-500 bg-red-500/10',
      'assist': 'text-blue-500 bg-blue-500/10',
      'save': 'text-green-500 bg-green-500/10',
      'default': 'text-gray-400 bg-gray-400/10'
    };
    return colors[activityType] || colors.default;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="bg-card border-border animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="h-12 w-12 bg-gray-700 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!followedUsers.length) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Followed Users</h3>
          <p className="text-muted-foreground mb-4">
            Start following other users to see their activities in your feed
          </p>
          <Button 
            variant="outline"
            onClick={() => navigate('/find-people')}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Find People to Follow
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!activities.length) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6 text-center">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Recent Activity</h3>
          <p className="text-muted-foreground">
            No recent activities from the people you follow
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => {
        const Icon = getActivityIcon(activity.activity_type);
        const colorClasses = getActivityColor(activity.activity_type);
        
        return (
          <Card key={activity.id} className="bg-card border-border hover:border-border/60 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <SecureProfilePhoto
                  filePath={activity.user_avatar}
                  fallbackInitials={activity.user_name?.charAt(0) || 'U'}
                  size="md"
                  className="h-16 w-16"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`p-2 rounded-lg ${colorClasses}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        {activity.user_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {activity.user_role && (
                          <span className="capitalize">{activity.user_role} • </span>
                        )}
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-foreground mb-1">
                      {activity.activity_title}
                    </h4>
                    {activity.activity_description && (
                      <p className="text-muted-foreground text-sm">
                        {activity.activity_description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <Button variant="ghost" size="sm" className="h-8 px-2">
                      <Heart className="h-4 w-4 mr-1" />
                      Like
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 px-2">
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Comment
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 px-2">
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};